/**
 * Study editing operations — create, load, update, save working studies.
 */

import type { Study, WorkingStudy, StudyGroup } from '@skjv/common';

/** Dependencies for study editing operations */
export interface StudyEditingDeps {
  studies: Study[];
  studyGroups: StudyGroup[];
  editingStudy: WorkingStudy | null;
  sortedStudies: Study[];
  sortedStudyGroups: StudyGroup[];
  generateId: () => string;
  now: () => string;
  saveStudies: () => void;
  saveStudyGroups: () => void;
}

/** Start a new study with defaults */
export function startNewStudy(deps: StudyEditingDeps): WorkingStudy {
  // Default to the last study's group; a first-ever study (the tour's
  // path) falls back to the most recently used group — the one just made
  const lastUsedGroupId =
    deps.studies.length > 0
      ? deps.sortedStudies[0].studyGroupId
      : deps.sortedStudyGroups[0]?.id;

  return {
    id: '',
    name: '',
    studyGroupId: lastUsedGroupId,
    scenes: [{ descriptions: [''] }],
    assignments: [],
  };
}

/** Save the current editing study — returns the ID or null on failure */
export function saveEditingStudy(deps: StudyEditingDeps): {
  id: string | null;
  studies: Study[];
  studyGroups: StudyGroup[];
  editingStudy: WorkingStudy | null;
} {
  const editing = deps.editingStudy;
  if (!editing) return { id: null, studies: deps.studies, studyGroups: deps.studyGroups, editingStudy: null };

  const { id, name, startingLocation, studyGroupId, scenes, assignments, teacherAssignments, writingLocation } = editing;
  if (!name || !startingLocation || !studyGroupId) {
    return { id: null, studies: deps.studies, studyGroups: deps.studyGroups, editingStudy: editing };
  }

  const now = deps.now();

  // Update study group's lastUsedAt
  const updatedGroups = deps.studyGroups.map((g) =>
    g.id === studyGroupId ? { ...g, lastUsedAt: now, updatedAt: now } : g,
  );

  const studyFields = {
    name,
    startingLocation,
    endingLocation: editing.endingLocation,
    studyGroupId,
    scenes: scenes || [],
    writingLocation: writingLocation || startingLocation,
    memoryStartingLocation: editing.memoryStartingLocation,
    memoryEndingLocation: editing.memoryEndingLocation,
    assignments: assignments || [],
    teacherAssignments: teacherAssignments || [],
    aiCommentary: editing.aiCommentary,
    readerThoughts: editing.readerThoughts,
  };

  if (!id) {
    const newId = deps.generateId();
    const newStudy: Study = { id: newId, createdAt: now, updatedAt: now, ...studyFields };
    return {
      id: newId,
      studies: [...deps.studies, newStudy],
      studyGroups: updatedGroups,
      editingStudy: { ...editing, id: newId },
    };
  }

  const updatedStudies = deps.studies.map((s) =>
    s.id === id ? { ...s, ...studyFields, updatedAt: now } : s,
  );

  return {
    id,
    studies: updatedStudies,
    studyGroups: updatedGroups,
    editingStudy: editing,
  };
}
